import { useApolloClient } from '@apollo/client'
import * as Sentry from '@sentry/nextjs'
import { message } from 'antd'
import { RcFile } from 'antd/lib/upload'
import { UploadFile } from 'antd/lib/upload/interface'
import Compressor from 'compressorjs'
import ExifReader from 'exifreader'
import moment from 'moment-timezone'

import {
  formatAccept,
  MAX_SIZE_B,
  MAX_SIZE_MB,
} from '@/config/constants/fileTypes'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { useUser } from '@/context/userContext'
import { extensionFromUrl } from '@/modules/attachments/attachmentHelpers'
import {
  CREATE_ATTACHMENT,
  CREATE_PUBLIC_ATTACHMENT,
  CREATE_ATTACHMENT_UPLOAD_URL,
} from '@/modules/attachments/graphQL'
import { uploadWithProgress } from '@/utils/index'

import {
  AttachmentType,
  CreateAttachmentUiMutation,
  CreateAttachmentUiMutationVariables,
  CreateAttachmentUploadUrlUiMutation,
  CreateAttachmentUploadUrlUiMutationVariables,
  CreatePublicAttachmentUiMutation,
  CreatePublicAttachmentUiMutationVariables,
  ImageCompressionPreset,
  Resource,
} from 'types/api.graphql'

type Options = {
  onUpload: OnUpload
  attachmentType: AttachmentType
  createAttachment: boolean
  attachmentResource: Resource
  attachmentSortDate?: string
  onProgress?: OnProgress
  isPublic?: boolean
  mutationVariables?: Partial<CreateAttachmentUiMutationVariables['input']>
  compressionOptions?: AttachmentCompressionOptions
}

export type AttachmentCompressionOptions = {
  preset?: ImageCompressionPreset
}

export type FileUploadResult = UploadFile & {
  attachmentId?: string
  key?: string
  exifMetadata?: JSON
}

type OnUpload = (result: FileUploadResult) => void
type OnProgress = (event: unknown, file: RcFile | File) => void

const standardImageSizeCompression = {
  maxWidth: 1920,
  maxHeight: 1080,
}

const highImageSizeCompression = {
  maxWidth: 3840,
  maxHeight: 2160,
}

const getLastModified = (file: RcFile | File) => {
  if ((file as RcFile).lastModifiedDate) {
    return (file as RcFile).lastModifiedDate
  }
  if (file.lastModified) {
    return new Date(file.lastModified)
  }

  return undefined
}

const imagePresets = {
  standard: standardImageSizeCompression,
  high: highImageSizeCompression,
  original: {}, // Empty object means no compression
}

export const useAttachmentUpload = () => {
  const client = useApolloClient()
  const { builderId, config } = useTemplateRenderer() || {}
  const { builder, gallerySettings } = useUser() || {}

  if (typeof window === 'undefined') {
    return {
      uploadAttachment: () => Promise.resolve(),
      validateFileSize: () => () => true,
      validateFileType: () => () => true,
    }
  }

  // This tracks the S3 upload progress which we arbitrarily limit to 80% of the total to indicate the post processing that will happen next
  const calcAdjustedS3Percent = (event) =>
    Math.floor((event.loaded / event.total) * 80)

  const compressImage = async (
    file: File,
    options: AttachmentCompressionOptions = {}
  ): Promise<RcFile> => {
    const { preset } = options || {}
    // This is to stagger the compression of images to avoid memory issues on Safari on iOS
    const randomDelay = Math.floor(Math.random() * 200)

    const presetToUse =
      preset || gallerySettings?.defaultCompression || 'standard'

    if (presetToUse === 'original') {
      return Promise.resolve(file as RcFile)
    }

    const sizePresetOptions = imagePresets[presetToUse]

    await new Promise((r) => setTimeout(r, randomDelay))
    return new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 1.0,
        ...sizePresetOptions,

        success(file) {
          resolve(file as RcFile)
        },
        error: reject,
      })
    })
  }

  const uploadAttachment = async (
    uploadedFile: RcFile | File,
    options: Options
  ) => {
    try {
      let exifMetadata = null
      let exifDate = null
      const file = uploadedFile

      const {
        attachmentType,
        attachmentResource,
        attachmentSortDate,
        mutationVariables,
      } = options
      const { onUpload, onProgress } = options

      const sanitizeExifMetadata = (exifMetadata: ExifReader.Tags): JSON => {
        const DESIRED_EXIF_TAGS = [
          'Make',
          'Model',
          'DateTime',
          'OffsetTime',
          'DateTimeOriginal',
          'OffsetTimeOriginal',
          'Software',
          'LensModel',
          'ExposureTime',
          'ApertureValue',
          'ShutterSpeedValue',
          'SceneType',
          'GPSAltitude',
          'GPSLatitude',
          'GPSLongitude',
          'GPSAltitudeRef',
          'Image Width',
          'Image Height',
          'SubjectArea',
        ]

        const filteredMetadata = Object.keys(exifMetadata)
          .filter((key) => DESIRED_EXIF_TAGS.includes(key))
          .reduce((obj, key) => {
            obj[key] = exifMetadata[key]
            return obj
          }, {})

        const jsonText = JSON.stringify(filteredMetadata)
        // replace '\\u0000' with empty string - This unicode char breaks Postgres
        const escaped = jsonText.replace(/\\u0000/g, '')
        return JSON.parse(escaped)
      }

      const getExifDate = (exifMetadata) => {
        const dateTimeOriginal = exifMetadata?.DateTimeOriginal?.description
        const timeZone = exifMetadata?.OffsetTimeOriginal?.description

        if (dateTimeOriginal && timeZone) {
          const date = moment(dateTimeOriginal, 'YYYY:MM:DD HH:mm:ss')

          return date.utcOffset(timeZone).toISOString()
        }

        return undefined
      }

      // Pre-compress image (only) prior to upload and presign
      // initialize the image
      let compressedFile = file

      if (file.type.includes('image')) {
        if (attachmentType !== AttachmentType.Signature) {
          // Extract exif metadata
          try {
            const rawMetadata = await ExifReader.load(file)
            exifMetadata = sanitizeExifMetadata(rawMetadata)
            exifDate = getExifDate(exifMetadata)
          } catch (e) {
            Sentry.captureException(e, {
              extra: {
                message: 'Error extracting exif metadata',
                options,
                config,
                flatOptions: JSON.stringify(options),
                flatConfig: JSON.stringify(config),
              },
            })
          }
        }

        // compress the image
        try {
          const imageOptions = options?.compressionOptions
          compressedFile = await compressImage(file, imageOptions)
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              message: 'Error compressing file',
              options,
              config,
              flatOptions: JSON.stringify(options),
              flatConfig: JSON.stringify(config),
            },
          })
        }
      }

      // step 1 - get upload url and key
      const presignedUrl = await client.mutate<
        CreateAttachmentUploadUrlUiMutation,
        CreateAttachmentUploadUrlUiMutationVariables
      >({
        mutation: CREATE_ATTACHMENT_UPLOAD_URL,
        variables: {
          input: {
            type: attachmentType,
            fileName: compressedFile.name,
            fileSize: `${compressedFile.size}`,
            contentType: compressedFile.type,
          },
        },
      })

      const { uploadUrl, key, url } =
        presignedUrl.data.createAttachmentUploadUrl

      const onUploadProgress = (e: ProgressEvent) => {
        onProgress?.({ percent: calcAdjustedS3Percent(e) }, file)
      }

      // step 2 - upload file to S3
      await uploadWithProgress(
        uploadUrl,
        compressedFile,
        onUploadProgress,
        true
      )

      // After successful S3 upload - show 90%
      onProgress?.({ percent: 90 }, uploadedFile)

      let id = null
      const isPublic = config?.isPublic || options.isPublic || false

      if (options.createAttachment && !isPublic) {
        // step 3 - create attachment record
        const attachment = await client.mutate<
          CreateAttachmentUiMutation,
          CreateAttachmentUiMutationVariables
        >({
          mutation: CREATE_ATTACHMENT,
          variables: {
            input: {
              key,
              type: attachmentType,
              resource: attachmentResource,
              fileName: compressedFile.name,
              fileSize: `${compressedFile.size}`,
              contentType: compressedFile.type,
              builderId: builderId || builder?.id,
              photoTakenDate: exifDate ? exifDate : undefined,
              sortDate: attachmentSortDate,
              exifMetadata: exifMetadata ? exifMetadata : undefined,
              ...mutationVariables,
            },
          },
        })

        id = attachment.data.createAttachment.id

        // After successful attachment creation - show 95%
        onProgress?.({ percent: 95 }, uploadedFile)
      }

      if (options.createAttachment && isPublic) {
        // step 3 - create attachment record
        const attachment = await client.mutate<
          CreatePublicAttachmentUiMutation,
          CreatePublicAttachmentUiMutationVariables
        >({
          mutation: CREATE_PUBLIC_ATTACHMENT,
          variables: {
            input: {
              key,
              type: attachmentType,
              resource: attachmentResource,
              fileName: compressedFile.name,
              fileSize: `${compressedFile.size}`,
              contentType: compressedFile.type,
              builderId: builderId || builder?.id,
              photoTakenDate: exifDate ? exifDate : undefined,
              exifMetadata: exifMetadata ? exifMetadata : undefined,
            },
          },
        })

        id = attachment.data.createPublicAttachment.id

        // After successful attachment creation - show 96%
        onProgress?.({ percent: 96 }, uploadedFile)
      }

      const uid = (file as RcFile).uid

      // step 4 - let the world know
      onUpload({
        key,
        url,
        uid, // matches the uid of the file showing upload progress
        attachmentId: id,
        name: compressedFile.name,
        size: compressedFile.size,
        type: compressedFile.type,
        status: 'done',
        lastModifiedDate: exifDate ? exifDate : getLastModified(file),
        exifMetadata: exifMetadata ? exifMetadata : undefined,
        originFileObj: compressedFile as RcFile,
      })

      // After successful attachment creation - show 100%
      onProgress?.({ percent: 100 }, uploadedFile)
    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          message: 'Error uploading attachment',
          options,
          config,
          flatOptions: JSON.stringify(options),
          flatConfig: JSON.stringify(config),
        },
      })

      message.error('There was an unexpected error uploading your file')
    }
  }

  const validateFileSize = (onError?: () => void) => (file: RcFile) => {
    if (file.size > MAX_SIZE_B) {
      message.error(
        `File size limit reached. Files must be less than ${MAX_SIZE_MB}MB`
      )
      onError?.()
      return false
    }
    if (file.size === 0) {
      message.error(
        'The file you are trying to upload is empty (0 bytes). Please choose a valid file.'
      )
      onError?.()
      return false
    }
    return true
  }

  const validateFileType = (accept: string) => (file: RcFile) => {
    if (!accept?.length) return true

    const extension = extensionFromUrl(file.name)

    if (!extension) {
      message.error('File type not supported')
      return false
    }

    if (!accept.includes(extension.toLowerCase())) {
      message.error(
        `File type not supported. Please upload a ${formatAccept(accept)} file`
      )
      return false
    }
    return true
  }

  return {
    uploadAttachment,
    validateFileSize,
    validateFileType,
  }
}
